/**
 * Timestamp formatting for the history view (B3.1) + state timeline (B3.2).
 * Pure + dependency-free; one place so both surfaces format identically.
 *
 * Issue #763. The gallery's history rows read "May 18 · 09:14" (absolute date
 * · 24h time); the timeline ticks read "May 18" (date only). We keep the
 * absolute stamp as the primary read (provenance wants the actual moment, not
 * a fuzzy "2 days ago") and offer a relative phrase for the row's title
 * tooltip.
 */

/**
 * "May 18 · 09:14" — an absolute date + 24h minute-precision time.
 *
 * Used by the history row (#763) and the per-node Notes thread (#906). Pass
 * `tz` (an IANA zone name, e.g. `"Asia/Singapore"`) to render the stamp in
 * the team's local time rather than the viewer's; omit it to keep the
 * original behaviour (the viewer's locale + zone) for existing callers.
 */
export function formatEventStamp(iso: string, tz?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // `timeZone: undefined` is valid for `Intl` and means "the runtime's zone",
  // so the no-tz path is byte-identical to the original `toLocale*` calls.
  const date = d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: tz,
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: tz,
  });
  return `${date} · ${time}`;
}

/** "May 18" — the timeline tick label (date only). */
export function formatDateTick(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * A COMPACT relative cue ("now", "5m", "3h", "2d", "3w", "May 18") for a tight
 * inline badge — the card's "🗒 <…>" notes-recency indicator (#1043, item 8).
 * Pure + calm; pass `now` (pinned in tests) to keep it deterministic. Returns
 * "" for an unparseable / future stamp so the caller can fall back gracefully.
 */
export function formatRelativeShort(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diffMs = now.getTime() - d.getTime();
  if (diffMs < 0) return "now";
  const minMs = 60 * 1000;
  const hourMs = 60 * minMs;
  const dayMs = 24 * hourMs;
  const weekMs = 7 * dayMs;
  if (diffMs < minMs) return "now";
  if (diffMs < hourMs) return `${Math.floor(diffMs / minMs)}m`;
  if (diffMs < dayMs) return `${Math.floor(diffMs / hourMs)}h`;
  if (diffMs < weekMs) return `${Math.floor(diffMs / dayMs)}d`;
  // ≥ a week ago drops to a coarse absolute date — a "5w" reads odd, an actual
  // date is clearer once a note thread is genuinely stale.
  if (diffMs < 4 * weekMs) return `${Math.floor(diffMs / weekMs)}w`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * A coarse relative phrase ("today", "yesterday", "3 days ago") for a row's
 * hover title. Calm + factual; never used as the primary read.
 */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const dayMs = 24 * 60 * 60 * 1000;
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(d)) / dayMs);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
